import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getSummary, getProperties, getBookingStats, getAnalytics, getMonthlyReport, getReceivables, getDashboardAlerts } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  Home,
  Users,
  BedDouble,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  AlertTriangle,
  Phone,
  CalendarCheck,
  IndianRupee,
  UserCheck,
  UserMinus,
  CalendarClock,
  BarChart3,
  FileText,
  TrendingDown,
  Wallet,
  CreditCard
} from 'lucide-react';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Past months = green; current (last) month = red
const BAR_PALETTE_PAST = {
  front: 'linear-gradient(180deg,#86efac 0%,#16a34a 55%,#14532d 100%)',
  side: '#166534',
  top: '#bbf7d0',
  glow: 'rgba(22,163,74,0.45)'
};
const BAR_PALETTE_CURRENT = {
  front: 'linear-gradient(180deg,#fca5a5 0%,#dc2626 55%,#7f1d1d 100%)',
  side: '#991b1b',
  top: '#fecaca',
  glow: 'rgba(220,38,38,0.5)'
};

const formatMoney = (n) => {
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n/100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n/1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const RevenueChart = ({ data }) => {
  const max = Math.max(...data.map(d => d.total || 0), 1);
  const totalSum = data.reduce((s, d) => s + (d.total || 0), 0);
  const avg = data.length > 0 ? totalSum / data.length : 0;

  return (
    <Card className="dash-animate" style={{ animationDelay: '500ms' }} data-testid="dashboard-revenue-chart">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
            Revenue (Last {data.length} Month{data.length !== 1 ? 's' : ''})
          </CardTitle>
          <div className="flex flex-col items-end">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">{data.length}M Average</span>
            <span className="font-mono font-bold text-slate-900 text-base">{formatMoney(avg)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm">No data yet</div>
        ) : (
          <div className="relative">
            {/* Subtle grid lines for depth perception */}
            <div className="absolute inset-x-0 top-6 sm:top-7 h-40 sm:h-48 pointer-events-none">
              <div className="h-full grid grid-rows-4">
                <div className="border-b border-dashed border-slate-200"></div>
                <div className="border-b border-dashed border-slate-200"></div>
                <div className="border-b border-dashed border-slate-200"></div>
                <div className="border-b border-dashed border-slate-200"></div>
              </div>
            </div>
            <div className="relative flex items-stretch justify-between gap-3 sm:gap-5 h-56 sm:h-64 pl-1 pr-2">
              {data.map((d, idx) => {
                const heightPct = max > 0 ? Math.max((d.total / max) * 100, d.total > 0 ? 8 : 2) : 2;
                const [year, mon] = (d.month || '').split('-');
                const label = mon ? MONTH_NAMES[parseInt(mon, 10) - 1] : '';
                const yearShort = year ? `'${year.slice(2)}` : '';
                const isLast = idx === data.length - 1;
                // Green for past months, red for current
                const palette = isLast ? BAR_PALETTE_CURRENT : BAR_PALETTE_PAST;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center min-w-0 h-full group">
                    {/* Amount label above bar */}
                    <div className="h-5 sm:h-7 flex items-end justify-center">
                      <span
                        className="text-[10px] sm:text-xs font-mono font-bold tracking-tight px-1.5 py-0.5 rounded-md transition-all group-hover:scale-110 group-hover:shadow-sm"
                        style={{
                          color: d.total > 0 ? '#0f172a' : '#94a3b8',
                          background: d.total > 0 ? 'rgba(255,255,255,0.95)' : 'transparent',
                        }}
                      >
                        {d.total > 0 ? formatMoney(d.total) : '—'}
                      </span>
                    </div>
                    {/* Flat 2D bar */}
                    <div className="w-full flex-1 flex flex-col justify-end px-1.5 sm:px-2.5">
                      <div
                        className={`relative w-full rounded-t-md transition-all duration-300 cursor-pointer hover:opacity-90 ${
                          isLast ? 'bg-red-500' : 'bg-emerald-500'
                        }`}
                        style={{ height: `${heightPct}%`, minHeight: d.total > 0 ? '14px' : '4px' }}
                        title={`${label}${yearShort}: ${formatMoney(d.total)}`}
                      ></div>
                    </div>
                    {/* X-axis label */}
                    <div className="h-6 mt-2 flex items-center justify-center w-full">
                      <span
                        className={`text-[11px] sm:text-sm font-bold tracking-wide truncate transition-colors ${
                          isLast ? 'text-red-700' : 'text-slate-700 group-hover:text-slate-900'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const ReceivablesCard = ({ data, loading }) => {
  const monthLabel = data?.current_month
    ? `${MONTH_FULL[parseInt(data.current_month.split('-')[1], 10) - 1]} ${data.current_month.split('-')[0]}`
    : '';
  const rupee = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

  const rows = [
    { key: 'raised', label: 'Dues Raised This Month', value: data?.dues_raised, sign: '', tone: 'text-amber-600' },
    { key: 'collected', label: 'Collected This Month', value: data?.collected, sign: '−', tone: 'text-emerald-600' },
  ];

  return (
    <Card className="dash-animate h-full" style={{ animationDelay: '520ms' }} data-testid="dashboard-receivables-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
            Receivables Summary
          </CardTitle>
          {monthLabel && <span className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">{monthLabel}</span>}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3 py-2" data-testid="receivables-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : !data ? (
          <div className="text-center py-10 text-slate-400 text-sm">Unable to load receivables</div>
        ) : (
          <div className="space-y-2.5">
            {rows.map((r) => (
              <div
                key={r.key}
                className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 px-4 py-3"
                data-testid={`receivables-${r.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {r.sign && <span className={`text-lg font-bold ${r.tone} w-4 text-center shrink-0`}>{r.sign}</span>}
                  {!r.sign && <span className="w-4 shrink-0" />}
                  <span className="text-sm text-slate-600 truncate">{r.label}</span>
                </div>
                <span className={`font-mono font-bold text-base ${r.tone} shrink-0`}>{rupee(r.value)}</span>
              </div>
            ))}

            {/* Closing total = Total Outstanding */}
            <div
              className="flex items-center justify-between rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3.5 mt-1 shadow-sm"
              data-testid="receivables-closing"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg font-bold text-white w-4 text-center shrink-0">=</span>
                <span className="text-sm font-semibold text-white truncate">Total Outstanding (Closing Dues)</span>
              </div>
              <span className="font-mono font-extrabold text-lg text-white shrink-0">{rupee(data.closing_dues)}</span>
            </div>

            {/* 4-way bifurcation of Total Outstanding */}
            <div
              className="grid grid-cols-4 gap-2 px-1 pt-1"
              data-testid="receivables-closing-breakdown"
            >
              {[
                { key: 'rent', label: 'Rent', value: data.closing_rent, tone: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-100' },
                { key: 'electricity', label: 'Electricity', value: data.closing_electricity, tone: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-100' },
                { key: 'deposit', label: 'Deposit', value: data.closing_deposit, tone: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100' },
                { key: 'penalty', label: 'Penalty', value: data.closing_penalty, tone: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-100' },
              ].map((b) => (
                <div
                  key={b.key}
                  className={`rounded-xl ${b.bg} border ${b.border} px-2.5 py-2 flex flex-col items-center text-center`}
                  data-testid={`receivables-closing-${b.key}`}
                >
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{b.label}</span>
                  <span className={`font-mono font-bold text-sm ${b.tone} mt-0.5 truncate max-w-full`}>{rupee(b.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [bookingStats, setBookingStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [receivablesLoading, setReceivablesLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [loading, setLoading] = useState(true);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const rupee = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

  // Receivables is heavier to compute — load it independently so it never blocks the dashboard.
  // background=true skips the skeleton (used for silent real-time refreshes).
  const loadReceivables = useCallback((background = false) => {
    if (!background) setReceivablesLoading(true);
    return getReceivables(selectedProperty === 'all' ? null : selectedProperty)
      .then((res) => setReceivables(res.data))
      .catch(() => { if (!background) setReceivables(null); })
      .finally(() => { if (!background) setReceivablesLoading(false); });
  }, [selectedProperty]);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, propsRes, bookingStatsRes, analyticsRes, monthlyRes, alertsRes] = await Promise.all([
        getSummary(selectedProperty === 'all' ? null : selectedProperty),
        getProperties(),
        getBookingStats(),
        getAnalytics(6),
        getMonthlyReport(currentMonth),
        getDashboardAlerts()
      ]);
      setSummary(summaryRes.data);
      setProperties(propsRes.data);
      setBookingStats(bookingStatsRes.data);
      setAnalytics(analyticsRes.data);
      setMonthlyReport(monthlyRes.data);
      setAlerts(alertsRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadReceivables();
  }, [loadReceivables]);

  // Keep the dashboard (and Receivables card) real-time: silently refresh whenever
  // the user returns to the tab/window (e.g., after recording a payment elsewhere).
  useEffect(() => {
    const refresh = () => { fetchData(); loadReceivables(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData, loadReceivables]);

  // Top stat tiles — order: Properties, Rooms, Beds, Occupied, Notice, Vacant, Bookings, Active Guests
  const stats = [
    { label: 'Properties',    value: summary?.total_properties || 0,  icon: Building,      grad: 'from-blue-500 to-indigo-600',     ring: 'ring-blue-200' },
    { label: 'Rooms',         value: summary?.total_rooms || 0,       icon: Home,          grad: 'from-slate-500 to-slate-700',     ring: 'ring-slate-200' },
    { label: 'Beds',          value: summary?.total_beds || 0,        icon: BedDouble,     grad: 'from-purple-500 to-fuchsia-600',  ring: 'ring-purple-200' },
    { label: 'Occupied',      value: summary?.occupied_beds || 0,     icon: CheckCircle,   grad: 'from-emerald-500 to-teal-600',    ring: 'ring-emerald-200' },
    { label: 'Notice',        value: summary?.notice_beds || 0,       icon: AlertTriangle, grad: 'from-amber-500 to-orange-600',     ring: 'ring-amber-200' },
    { label: 'Vacant',        value: summary?.vacant_beds || 0,       icon: AlertCircle,   grad: 'from-rose-500 to-pink-600',       ring: 'ring-rose-200' },
    { label: 'Bookings',      value: summary?.total_bookings || 0,    icon: CalendarCheck, grad: 'from-blue-500 to-indigo-600',     ring: 'ring-blue-200' },
    { label: 'Active Guests', value: summary?.total_guests || 0,      icon: UserCheck,     grad: 'from-emerald-500 to-teal-600',    ring: 'ring-emerald-200' },
    { label: 'Occupancy',     value: `${summary?.occupancy_rate || 0}%`, icon: TrendingUp,  grad: 'from-cyan-500 to-sky-600',      ring: 'ring-cyan-200' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Overview of your accommodations</p>
        </div>
        <Select value={selectedProperty} onValueChange={setSelectedProperty}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="property-filter">
            <SelectValue placeholder="Filter by property" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {properties.map((prop) => (
              <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Grid - Compact & Attractive */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2.5 sm:gap-3">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className={`dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-3 sm:p-3.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ${stat.ring}`}
            style={{ animationDelay: `${idx * 45}ms` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {/* gradient corner accent */}
            <div className={`absolute -top-6 -right-6 w-16 h-16 rounded-full bg-gradient-to-br ${stat.grad} opacity-10`}></div>
            <div className="relative flex items-start gap-2">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br ${stat.grad} flex items-center justify-center shrink-0 shadow-sm`}>
                <stat.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wide leading-tight truncate">{stat.label}</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold font-mono text-slate-900 mt-0.5 leading-none">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alert Tables: Rent+Electricity Due by Date + Needs Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-alerts">
        {/* Rent + Electricity Due by Due Date */}
        <Card className="dash-animate" style={{ animationDelay: '360ms' }} data-testid="due-by-date-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <CalendarClock className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                Dues Collection Schedule
              </CardTitle>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Current dues</span>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Due Window</TableHead>
                  <TableHead className="text-center">Guests</TableHead>
                  <TableHead className="text-right">Rent + Electricity Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(alerts?.due_buckets || []).map((b, idx) => {
                  const tones = ['bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
                  return (
                    <TableRow key={b.range} data-testid={`due-bucket-${b.range}`}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${tones[idx] || 'bg-slate-400'}`}></span>
                          {b.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono text-slate-600">{b.guest_count}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-slate-900">{rupee(b.rent_electricity_due)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-slate-100 font-bold" data-testid="due-bucket-total">
                  <TableCell className="text-right">TOTAL</TableCell>
                  <TableCell className="text-center font-mono">{alerts?.due_buckets_total?.guest_count || 0}</TableCell>
                  <TableCell className="text-right font-mono text-indigo-700">{rupee(alerts?.due_buckets_total?.rent_electricity_due || 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Needs Attention */}
        <Card className="dash-animate" style={{ animationDelay: '400ms' }} data-testid="needs-attention-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              Needs Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">Guests</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow data-testid="attention-left-pending" className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/reports?tab=ledger')}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2"><UserMinus className="h-4 w-4 text-rose-500 shrink-0" />Left but Final Settlement Pending</span>
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold text-rose-600">{alerts?.left_pending?.count || 0}</TableCell>
                </TableRow>
                <TableRow data-testid="attention-overstay" className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/guests')}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-purple-500 shrink-0" />Overstay Guests</span>
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold text-purple-600">{alerts?.overstay?.count || 0}</TableCell>
                </TableRow>
                <TableRow data-testid="attention-advance-bookings" className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/bookings')}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-indigo-500 shrink-0" />Advance Bookings</span>
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold text-indigo-600">{(bookingStats?.total_pending || 0) + (bookingStats?.total_confirmed || 0)}</TableCell>
                </TableRow>
                <TableRow data-testid="attention-leaving-today" className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/guests')}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />Leaving Today</span>
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold text-amber-600">{summary?.leaving_today_count || 0}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Average Rent by Sharing */}
        <Card className="dash-animate" style={{ animationDelay: '440ms' }} data-testid="sharing-avg-rent-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <BedDouble className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                Sharing Wise Average Rent Per Bed
              </CardTitle>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Per occupied bed</span>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sharing</TableHead>
                  <TableHead className="text-center">Occupied</TableHead>
                  <TableHead className="text-right">Avg Rent</TableHead>
                  <TableHead className="text-center">Vacant</TableHead>
                  <TableHead className="text-right">Potential Rent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(alerts?.sharing_avg_rent || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-6">No active guests</TableCell></TableRow>
                ) : (alerts?.sharing_avg_rent || []).map((s) => (
                  <TableRow key={s.sharing_type} data-testid={`sharing-avg-${s.sharing_type}`}>
                    <TableCell className="font-medium">{s.label}</TableCell>
                    <TableCell className="text-center font-mono text-slate-600">{s.occupied_beds}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-700">{rupee(s.average_rent)}</TableCell>
                    <TableCell className="text-center font-mono text-rose-500">{s.vacant_beds}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-red-600">{rupee(s.potential_rent)}</TableCell>
                  </TableRow>
                ))}
                {(alerts?.sharing_avg_rent || []).length > 0 && (
                  <TableRow className="bg-slate-100 font-bold" data-testid="sharing-avg-total">
                    <TableCell className="text-right">TOTAL</TableCell>
                    <TableCell className="text-center font-mono">{alerts.sharing_avg_rent.reduce((s, x) => s + (x.occupied_beds || 0), 0)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{(() => { const occ = alerts.sharing_avg_rent.reduce((s, x) => s + (x.occupied_beds || 0), 0); const tot = alerts.sharing_avg_rent.reduce((s, x) => s + (x.total_rent || 0), 0); return rupee(occ ? tot / occ : 0); })()}</TableCell>
                    <TableCell className="text-center font-mono text-rose-600">{alerts.sharing_avg_rent.reduce((s, x) => s + (x.vacant_beds || 0), 0)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{rupee(alerts.sharing_avg_rent.reduce((s, x) => s + (x.potential_rent || 0), 0))}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Mini Revenue Chart - Last 6 Months (moved below Room Distribution) */}

      {/* Room Distribution - Compact */}
      <Card className="dash-animate" style={{ animationDelay: '420ms' }}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Home className="h-4 w-4 sm:h-5 sm:w-5" />
            Room Distribution by Sharing Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary?.sharing_distribution && Object.keys(summary.sharing_distribution).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 sm:gap-3">
              {Object.entries(summary.sharing_distribution).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([type, data]) => {
                const occupancyPct = data.total_beds > 0 ? Math.round((data.occupied / data.total_beds) * 100) : 0;
                return (
                  <div
                    key={type}
                    className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm hover:shadow-md transition-all duration-200"
                    data-testid={`sharing-tile-${type.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-900 text-sm">{type}</h4>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-mono">{occupancyPct}%</Badge>
                    </div>
                    {/* Occupancy bar */}
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden mb-2.5">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all"
                        style={{ width: `${occupancyPct}%` }}
                      ></div>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:text-xs">
                      <div className="flex flex-col">
                        <span className="text-slate-400 text-[10px] uppercase tracking-wide">Rooms</span>
                        <span className="font-mono font-bold text-slate-900">{data.total_rooms}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-slate-400 text-[10px] uppercase tracking-wide">Beds</span>
                        <span className="font-mono font-bold text-slate-900">{data.total_beds}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-emerald-600 text-[10px] uppercase tracking-wide">Occupied</span>
                        <span className="font-mono font-bold text-emerald-600">{data.occupied}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-rose-500 text-[10px] uppercase tracking-wide">Vacant</span>
                        <span className="font-mono font-bold text-rose-600">{data.vacant}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Home className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No rooms added yet. Start by adding properties and rooms.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue chart + Receivables summary side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueChart data={analytics?.monthly_collections || []} />
        <ReceivablesCard data={receivables} loading={receivablesLoading} />
      </div>

      {/* Monthly Financial Report - current month (moved from Reports > Summary) */}
      {monthlyReport && (
        <Card className="dash-animate" style={{ animationDelay: '560ms' }} data-testid="dashboard-monthly-report">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
              Monthly Financial Report - {currentMonth}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2 text-emerald-700">
                  <TrendingUp className="h-4 w-4" />
                  Receipts
                </h4>
                <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-emerald-100">
                    <Wallet className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-emerald-600">Cash</span>
                    <span className="ml-auto font-mono font-bold text-emerald-700">
                      ₹{(monthlyReport.collections?.total_cash || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pb-2 border-b border-emerald-100">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-600">Bank</span>
                    <span className="ml-auto font-mono font-bold text-blue-700">
                      ₹{(monthlyReport.collections?.total_bank || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="ml-auto font-mono font-bold text-emerald-800 text-lg">
                      ₹{(monthlyReport.collections?.grand_total || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2 text-rose-700">
                  <TrendingDown className="h-4 w-4" />
                  Payments
                </h4>
                <div className="bg-rose-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-rose-100">
                    <Wallet className="h-4 w-4 text-rose-600" />
                    <span className="text-sm text-rose-600">Cash</span>
                    <span className="ml-auto font-mono font-bold text-rose-700">
                      ₹{(monthlyReport.expenses?.cash || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pb-2 border-b border-rose-100">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-600">Bank</span>
                    <span className="ml-auto font-mono font-bold text-blue-700">
                      ₹{(monthlyReport.expenses?.bank || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="ml-auto font-mono font-bold text-rose-800 text-lg">
                      ₹{(monthlyReport.expenses?.total || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2 text-purple-700">
                  <TrendingUp className="h-4 w-4" />
                  Net Balance
                </h4>
                <div className="bg-purple-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-purple-100">
                    <Wallet className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-purple-600">Cash</span>
                    <span className={`ml-auto font-mono font-bold ${((monthlyReport.collections?.total_cash || 0) - (monthlyReport.expenses?.cash || 0)) >= 0 ? 'text-purple-700' : 'text-red-700'}`}>
                      ₹{((monthlyReport.collections?.total_cash || 0) - (monthlyReport.expenses?.cash || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pb-2 border-b border-purple-100">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-600">Bank</span>
                    <span className={`ml-auto font-mono font-bold ${((monthlyReport.collections?.total_bank || 0) - (monthlyReport.expenses?.bank || 0)) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      ₹{((monthlyReport.collections?.total_bank || 0) - (monthlyReport.expenses?.bank || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-sm font-semibold">Total</span>
                    <span className={`ml-auto font-mono font-bold text-lg ${(monthlyReport.net_income || 0) >= 0 ? 'text-purple-800' : 'text-red-700'}`}>
                      ₹{(monthlyReport.net_income || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
